import type { NotUndefined } from '@_/types';
import type { OTPInputProps as OriginalOTPInputProps, SlotProps } from 'input-otp';
import type { MessageDescriptor } from 'react-intl';
import clsx from 'clsx';
import { OTPInput as OriginalOTPInput, REGEXP_ONLY_DIGITS } from 'input-otp';
import { useCallback } from 'react';
import { ErrorMessage, FormattedError } from '@_/ui';

export interface OTPInputProps extends Pick<
  OriginalOTPInputProps,
  'autoFocus' | 'disabled' | 'onBlur' | 'onChange' | 'onFocus' | 'placeholder' | 'value'
> {
  digits: number;
  error?: React.ReactNode | MessageDescriptor;
  onComplete?: (token: string) => void;
}

export function OTPInput({ digits, error, onComplete, ...rest }: OTPInputProps) {
  const handleComplete = useCallback<NotUndefined<OriginalOTPInputProps['onComplete']>>(
    value => onComplete?.(value),
    [onComplete]
  );

  const errorMessage =
    error && typeof error !== 'boolean' ? <FormattedError error={error} /> : undefined;

  return (
    <div>
      <OriginalOTPInput
        {...rest}
        containerClassName="group flex items-center has-[:disabled]:opacity-30"
        maxLength={digits}
        onComplete={handleComplete}
        pasteTransformer={pasteTransformer}
        pattern={REGEXP_ONLY_DIGITS}
        render={({ slots }) => (
          <>
            <div className="flex">
              {slots.slice(0, Math.floor(digits / 2)).map((slot, index) => (
                // oxlint-disable-next-line react/no-array-index-key
                <Slot key={index} error={!!error} {...slot} />
              ))}
            </div>

            <FakeDash />

            <div className="flex">
              {slots.slice(Math.floor(digits / 2)).map((slot, index) => (
                // oxlint-disable-next-line react/no-array-index-key
                <Slot key={index} error={!!error} {...slot} />
              ))}
            </div>
          </>
        )}
      />

      {!!errorMessage && (
        <div className="mt-4 text-center">
          <ErrorMessage message={errorMessage} plain />
        </div>
      )}
    </div>
  );
}

function Slot({
  char,
  error,
  hasFakeCaret,
  isActive,
  placeholderChar
}: SlotProps & { error?: boolean }) {
  return (
    <div
      className={clsx(
        'relative h-12 w-[2.4rem] text-[1.7rem] sm:h-14 sm:w-10 sm:text-[2rem]',
        'flex items-center justify-center',
        'transition-[border-color,outline-color] duration-150 ease-out motion-reduce:transition-none',
        'border-y border-r first:rounded-l-md first:border-l last:rounded-r-md',
        error ? 'border-red-600' : 'border-gray-200',
        'group-focus-within:border-gray-400 group-hover:border-gray-400',
        isActive ? 'outline-2 outline-primary-500' : 'outline-transparent'
      )}
    >
      <div className="group-has-[input[data-input-otp-placeholder-shown]]:opacity-20">
        {char ?? placeholderChar}
      </div>
      {hasFakeCaret && <FakeCaret />}
    </div>
  );
}

function FakeCaret() {
  return (
    <div className="pointer-events-none absolute inset-0 flex animate-caret-blink items-center justify-center motion-reduce:animate-none">
      <div className="h-8 w-px bg-black" />
    </div>
  );
}

function FakeDash() {
  return (
    <div className="flex w-8 items-center justify-center sm:w-10">
      <div className="h-1 w-3 rounded-full bg-gray-300" />
    </div>
  );
}

function pasteTransformer(pastedText: string) {
  return pastedText.replace(/\s/, '');
}
